import { AlertTriangle, CreditCard, FileText, Smartphone } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ActionHint from '../../components/analytics/ActionHint';
import DataQualityBadge from '../../components/analytics/DataQualityBadge';
import MetricDefinitionsDrawer from '../../components/analytics/MetricDefinitionsDrawer';
import MetricTooltip from '../../components/analytics/MetricTooltip';
import PageGuide from '../../components/analytics/PageGuide';
import { analyticsService } from '../../services/api';
import '../Analytics.css';
import AnalyticsLayout from './AnalyticsLayout';

function getDefaultFromDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
}

const formatNumber = (value) =>
    new Intl.NumberFormat('it-IT').format(value || 0);

function StatusBadge({ value, warnIfAbove, successIfZero, label }) {
    let color = '#64748b';
    let bg = '#f1f5f9';
    if (successIfZero && value === 0) { color = '#10b981'; bg = '#d1fae5'; }
    else if (warnIfAbove !== undefined && value > warnIfAbove) { color = '#ef4444'; bg = '#fee2e2'; }
    else if (value > 0) { color = '#f59e0b'; bg = '#fef3c7'; }
    return (
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color, background: bg, padding: '2px 8px', borderRadius: '12px' }}>
            {value} {label}
        </span>
    );
}

const cardLink = { cursor: 'pointer' };

export default function AnalyticsOperations() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        from: getDefaultFromDate(),
        to: new Date().toISOString().split('T')[0]
    });

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await analyticsService.getOperations({ from: filters.from, to: filters.to });
            setData(result);
        } catch (err) {
            console.error('Failed to load operations data:', err);
            setError(err.response?.data?.message || err.message || 'Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return (
        <AnalyticsLayout filters={filters} onFiltersChange={setFilters}>
            <PageGuide
                title="Salute operativa"
                summary="Problemi attivi su device, pagamenti, payout e fatturazione. I valori device sono in real-time; pagamenti e invoice sono filtrati per periodo."
            />
            {loading && !data && (
                <div className="analytics-loading">
                    <div className="spinner"></div>
                    <p>Caricamento dati operativi...</p>
                </div>
            )}

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    <strong>Errore:</strong> {error}
                </div>
            )}

            {data && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            Periodo: {data.period?.from} → {data.period?.to} · Dati device in real-time
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <MetricDefinitionsDrawer metricKeys={['activeDevices', 'offlineDevices', 'failedPayments', 'pendingPayments', 'invoiceRunFailures', 'workerKycCompletionRate']} />
                            <button className="btn-refresh" onClick={loadData} disabled={loading} style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
                                {loading ? '⏳' : '🔄'} Aggiorna
                            </button>
                        </div>
                    </div>

                    {/* ActionHints for detected issues */}
                    <ActionHint
                        condition={data.devices?.offline > 0}
                        severity="warning"
                        message={`${data.devices?.offline} device offline. Verifica lo stato nel pannello device e contatta i locali interessati.`}
                        actions={[{ label: 'Vai ai Device', href: '/admin/devices' }]}
                    />
                    <ActionHint
                        condition={data.payments?.failed > 0}
                        severity="warning"
                        message={`${data.payments?.failed} pagamenti falliti nel periodo (failure rate: ${data.payments?.failureRate || 0}%). Analizza le cause su Stripe o nel pannello transazioni.`}
                        actions={[{ label: 'Vai alle Transazioni', href: '/admin/tip-transactions' }]}
                    />
                    <ActionHint
                        condition={data.workerIssues?.stripeOnboardingIncomplete > 0}
                        severity="warning"
                        message={`${data.workerIssues?.stripeOnboardingIncomplete} percettori con Stripe Connect incompleto. Non possono ricevere payout finché non completano l'onboarding.`}
                    />
                    <ActionHint
                        condition={data.invoiceRuns?.issueCount > 0}
                        severity="error"
                        message={`${data.invoiceRuns?.issueCount} invoice run con errori nel periodo. Alcune fatture potrebbero non essere state generate o inviate.`}
                        actions={[{ label: 'Vai a Invoice Monitoring', href: '/admin/invoices/monitoring' }]}
                    />

                    {/* Summary alert row */}
                    {(data.devices?.offline > 0 || data.payments?.failed > 0 || data.workerIssues?.stripeOnboardingIncomplete > 0 || data.invoiceRuns?.issueCount > 0) && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '10px',
                            padding: '12px 16px',
                            background: '#fff7ed',
                            border: '1px solid #fed7aa',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            alignItems: 'center'
                        }}>
                            <AlertTriangle size={16} color="#f97316" />
                            <span style={{ fontSize: '0.8rem', color: '#9a3412', fontWeight: 500 }}>Elementi che richiedono attenzione:</span>
                            {data.devices?.offline > 0 && <StatusBadge value={data.devices.offline} warnIfAbove={0} label="device offline" />}
                            {data.payments?.failed > 0 && <StatusBadge value={data.payments.failed} warnIfAbove={0} label="pagamenti falliti" />}
                            {data.workerIssues?.stripeOnboardingIncomplete > 0 && <StatusBadge value={data.workerIssues.stripeOnboardingIncomplete} warnIfAbove={0} label="Stripe incompleto" />}
                            {data.invoiceRuns?.issueCount > 0 && <StatusBadge value={data.invoiceRuns.issueCount} warnIfAbove={0} label="invoice run con errori" />}
                        </div>
                    )}

                    {/* Device Health */}
                    <div className="kpi-section">
                        <h2 className="section-title">Salute Device</h2>
                        <div className="kpi-grid">
                            <div className="kpi-card kpi-info" style={cardLink} onClick={() => navigate('/admin/devices')}>
                                <div className="kpi-icon"><Smartphone size={28} /></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Totale Device</div>
                                    <div className="kpi-value">{data.devices?.total || 0}</div>
                                    <div className="kpi-meta">
                                        <span style={{ color: '#10b981' }}>✓ {data.devices?.active || 0} attivi</span>
                                    </div>
                                </div>
                            </div>
                            <div className={`kpi-card ${data.devices?.offline > 0 ? 'kpi-warning' : 'kpi-success'}`} style={cardLink} onClick={() => navigate('/admin/devices')}>
                                <div className="kpi-icon"><span style={{ fontSize: '24px' }}>📴</span></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Device Offline</div>
                                    <div className="kpi-value">{data.devices?.offline || 0}</div>
                                    <div className="kpi-meta">
                                        <span>Non in stato ACTIVE</span>
                                    </div>
                                </div>
                            </div>
                            <div className={`kpi-card ${data.devices?.staleSignalCount > 0 ? 'kpi-warning' : 'kpi-success'}`} style={cardLink} onClick={() => navigate('/admin/devices')}>
                                <div className="kpi-icon"><span style={{ fontSize: '24px' }}>📡</span></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Segnale Assente ({'>'}24h)</div>
                                    <div className="kpi-value">{data.devices?.staleSignalCount || 0}</div>
                                    <div className="kpi-meta">
                                        <span>Nessun heartbeat nelle ultime 24h</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Device by status breakdown */}
                        {data.devices?.byStatus && Object.keys(data.devices.byStatus).length > 0 && (
                            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {Object.entries(data.devices.byStatus).map(([status, count]) => (
                                    <div key={status} style={{
                                        background: '#f8fafc',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        padding: '6px 12px',
                                        fontSize: '0.8rem'
                                    }}>
                                        <span style={{ color: '#64748b' }}>{status}: </span>
                                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{formatNumber(count)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Payment Health */}
                    <div className="kpi-section">
                        <h2 className="section-title">Salute Pagamenti (periodo)</h2>
                        <div className="kpi-grid">
                            <div className="kpi-card kpi-info" style={cardLink} onClick={() => navigate('/admin/tip-transactions')}>
                                <div className="kpi-icon"><CreditCard size={28} /></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Tentativi eleggibili</div>
                                    <div className="kpi-value">{data.payments?.eligibleAttempts || 0}</div>
                                    <div className="kpi-meta"><span>succeeded + failed</span></div>
                                </div>
                            </div>
                            <MetricTooltip metricKey="failedPayments">
                                <div className={`kpi-card ${data.payments?.failed > 0 ? 'kpi-warning' : 'kpi-success'}`} style={cardLink} onClick={() => navigate('/admin/tip-transactions')}>
                                    <div className="kpi-icon"><span style={{ fontSize: '24px' }}>✗</span></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Pagamenti Falliti</div>
                                        <div className="kpi-value">{data.payments?.failed || 0}</div>
                                        <div className="kpi-meta">
                                            <span>{data.payments?.failureRate || 0}% failure rate</span>
                                        </div>
                                    </div>
                                </div>
                            </MetricTooltip>
                            {(data.payments?.pendingOtherCount || 0) > 0 && (
                                <div className="kpi-card kpi-secondary" style={cardLink} onClick={() => navigate('/admin/tip-transactions')}>
                                    <div className="kpi-icon"><span style={{ fontSize: '24px' }}>…</span></div>
                                    <div className="kpi-content">
                                        <div className="kpi-label">Altri stati</div>
                                        <div className="kpi-value">{data.payments?.pendingOtherCount || 0}</div>
                                        <div className="kpi-meta"><span>pending/canceled/processing...</span></div>
                                    </div>
                                </div>
                            )}
                            <div className={`kpi-card ${data.payments?.reversedTransfers > 0 ? 'kpi-warning' : 'kpi-success'}`} style={cardLink} onClick={() => navigate('/admin/tip-transactions')}>
                                <div className="kpi-icon"><span style={{ fontSize: '24px' }}>↩</span></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Transfer Reversati</div>
                                    <div className="kpi-value">{data.payments?.reversedTransfers || 0}</div>
                                    <div className="kpi-meta"><span>Nel periodo selezionato</span></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Worker & Invoice Issues */}
                    <div className="kpi-section">
                        <h2 className="section-title">
                            Problemi Worker &amp; Fatturazione
                            <span style={{ marginLeft: '10px' }}>
                                <DataQualityBadge status="needs_validation" note="Invoice data: verifica manuale consigliata" />
                            </span>
                        </h2>
                        <div className="kpi-grid">
                            <div className={`kpi-card ${data.workerIssues?.stripeOnboardingIncomplete > 0 ? 'kpi-warning' : 'kpi-success'}`}>
                                <div className="kpi-icon"><CreditCard size={28} /></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Stripe Incompleto</div>
                                    <div className="kpi-value">{data.workerIssues?.stripeOnboardingIncomplete || 0}</div>
                                    <div className="kpi-meta">
                                        <span>Onboarding avviato, non completato</span>
                                    </div>
                                </div>
                            </div>
                            <div className={`kpi-card ${data.invoiceRuns?.issueCount > 0 ? 'kpi-warning' : 'kpi-success'}`} style={cardLink} onClick={() => navigate('/admin/invoices/monitoring')}>
                                <div className="kpi-icon"><FileText size={28} /></div>
                                <div className="kpi-content">
                                    <div className="kpi-label">Invoice Run con Errori</div>
                                    <div className="kpi-value">{data.invoiceRuns?.issueCount || 0}</div>
                                    <div className="kpi-meta">
                                        <span>Nel periodo selezionato</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Invoice run issues table */}
                        {data.invoiceRuns?.recentIssues && data.invoiceRuns.recentIssues.length > 0 && (
                            <div className="trend-section" style={{ marginTop: '16px' }}>
                                <div className="trend-table-container">
                                    <table className="trend-table">
                                        <thead>
                                            <tr>
                                                <th>Mese</th>
                                                <th>Status</th>
                                                <th>Fatture Fallite</th>
                                                <th>Fatture Generate</th>
                                                <th>Avviato il</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.invoiceRuns.recentIssues.map(run => (
                                                <tr key={run.id}>
                                                    <td style={{ fontWeight: 500 }}>{run.yearMonth}</td>
                                                    <td>
                                                        <span style={{
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                            color: run.status === 'FAILED' ? '#ef4444' : '#f59e0b',
                                                            background: run.status === 'FAILED' ? '#fee2e2' : '#fef3c7',
                                                            padding: '2px 8px',
                                                            borderRadius: '10px'
                                                        }}>
                                                            {run.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ color: '#ef4444', fontWeight: 600 }}>{run.invoicesFailed}</td>
                                                    <td>{run.invoicesGenerated}</td>
                                                    <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                                        {new Date(run.startedAt).toLocaleString('it-IT')}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </AnalyticsLayout>
    );
}
